/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useState, useRef, useEffect, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import './SearchableSelect.css';
import { Icon } from './shared/ComponentIcon';
import { useFloatingDropdownMenu } from '../hooks/useFloatingDropdownMenu';


interface Option {
    value: string;
    label: string;
    icon?: string;
    description?: string;
    iconSrc?: string;
    iconDarkSrc?: string;
    searchStr?: string; // Optional custom search string if different from label
    disabled?: boolean;
    learningOptionKey?: string;
}

export interface SearchableSelectViewProps {
    labels: { searchPlaceholder: string; search: string; noData: string };
    options: Option[];
    value: string;
    onChange: (value: string) => void;
    /** Search notification for bounded caller-owned catalogs; reset to empty on close. */
    onSearchChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    searchable?: boolean;
    /** Compact single-line trigger for list filters; the open catalog keeps full labels. */
    density?: 'default' | 'compact';
    required?: boolean;
    id?: string;
    ariaLabel?: string;
    ariaDescribedBy?: string;
    ariaInvalid?: boolean;
    learningTargetId?: string;
}

const normalizeSearchText = (value: string): string =>
    value.toLocaleLowerCase('tr-TR').replace(/ı/g, 'i');

export const SearchableSelectView: React.FC<SearchableSelectViewProps> = ({
    options,
    value,
    onChange,
    onSearchChange,
    placeholder = 'Select...',
    disabled = false,
    searchable = true,
    density = 'default',
    required = false,
    id,
    ariaLabel,
    ariaDescribedBy,
    ariaInvalid = false,
    learningTargetId,
    labels,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeValue, setActiveValue] = useState<string | null>(null);
    const [requiredInvalid, setRequiredInvalid] = useState(false);
    const listId = useId();
    const triggerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { dropdownRef, dropdownStyle } = useFloatingDropdownMenu<HTMLDivElement>({
        open: isOpen,
        anchorRef: wrapperRef,
        minHeight: 140,
        maxHeight: 300,
    });

    // Handle outside clicks to close dropdown
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Node;
            if (
                wrapperRef.current?.contains(target) ||
                dropdownRef.current?.contains(target)
            ) {
                return;
            }
            setIsOpen(false);
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filter options based on search term
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        const lowerSearch = normalizeSearchText(searchTerm);
        return options.filter(option =>
            normalizeSearchText(option.searchStr || option.label).includes(lowerSearch)
        );
    }, [options, searchTerm]);

    // Find selected option object
    const selectedOption = options.find(opt => opt.value === value);
    // Filtering can render before the effect reconciles activeValue. Keyboard
    // selection and aria-activedescendant must use the same valid option now.
    const requestedActiveIndex = filteredOptions.findIndex(option => option.value === activeValue && !option.disabled);
    const selectedIndex = filteredOptions.findIndex(option => option.value === value && !option.disabled);
    const activeIndex = requestedActiveIndex >= 0 ? requestedActiveIndex
        : selectedIndex >= 0 ? selectedIndex : filteredOptions.findIndex(option => !option.disabled);
    const activeId = activeIndex < 0 ? undefined : `${listId}-${activeIndex}`;
    useEffect(() => {
        if (!isOpen) return;
        if (!filteredOptions.some(option => option.value === activeValue && !option.disabled)) {
            setActiveValue(filteredOptions.find(option => option.value === value && !option.disabled)?.value
                ?? filteredOptions.find(option => !option.disabled)?.value ?? null);
        }
    }, [filteredOptions, activeValue, isOpen, value]);
    useEffect(() => { if (disabled) setIsOpen(false); }, [disabled]);
    useEffect(() => {
        if (!required || disabled || value) setRequiredInvalid(false);
    }, [disabled, required, value]);
    useEffect(() => {
        if (isOpen && activeId) document.getElementById(activeId)?.scrollIntoView?.({ block: 'nearest' });
    }, [activeId, isOpen]);

    // Focus input when opening
    useEffect(() => {
        if (isOpen && dropdownStyle.visibility === 'visible' && searchable && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen, searchable, dropdownStyle.visibility]);
    useEffect(() => {
        if (!isOpen && searchTerm) {
            setSearchTerm('');
            onSearchChange?.('');
        }
    }, [isOpen, searchTerm, onSearchChange]);

    const handleSelect = (optionValue: string) => {
        const option = options.find(item => item.value === optionValue);
        if (disabled || !option || option.disabled) return;
        onChange(optionValue);
        setRequiredInvalid(false);
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    const handleListKey = (event: React.KeyboardEvent) => {
        if (disabled) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); event.stopPropagation();
            const enabled = filteredOptions.filter(option => !option.disabled);
            if (!enabled.length) return;
            const current = enabled.findIndex(option => option.value === activeValue);
            const next = current < 0 ? (event.key === 'ArrowUp' ? enabled.length - 1 : 0)
                : (current + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length;
            setActiveValue(enabled[next].value);
        } else if (event.key === 'Enter') {
            event.preventDefault(); event.stopPropagation();
            if (activeIndex >= 0) handleSelect(filteredOptions[activeIndex].value);
        } else if (event.key === 'Escape' || event.key === 'Tab') {
            if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); }
            setIsOpen(false); triggerRef.current?.focus();
        }
    };

    const renderOptionContent = (option: Option, includeDescription = true) => (
        <span className="searchable-select-option-content">
            {option.icon && !option.iconSrc && <Icon name={option.icon} size={18} />}
            {option.iconSrc && (
                <span className="searchable-select-option-icon-wrap" aria-hidden="true">
                    <img className={`searchable-select-option-icon${option.iconDarkSrc ? ' searchable-select-option-icon-light' : ''}`} src={option.iconSrc} alt="" />
                    {option.iconDarkSrc && <img className="searchable-select-option-icon searchable-select-option-icon-dark" src={option.iconDarkSrc} alt="" />}
                </span>
            )}
            <span className="searchable-select-option-text"><span>{option.label}</span>{includeDescription && option.description && <small className="searchable-select-option-description">{option.description}</small>}</span>
        </span>
    );

    const dropdownContent = isOpen && (
        <div
            ref={dropdownRef}
            className="qubepro-searchable-select searchable-select-dropdown searchable-select-dropdown-portal"
            style={dropdownStyle}
            onKeyDown={handleListKey}
        >
            {searchable && (
                <div className="searchable-select-search">
                    <Icon name="search" size={16} className="searchable-select-search-icon" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); onSearchChange?.(e.target.value); }}
                        placeholder={labels.searchPlaceholder}
                        role="combobox"
                        aria-label={`${ariaLabel || placeholder} — ${labels.search}`}
                        aria-expanded={true}
                        aria-controls={listId}
                        aria-autocomplete="list"
                        aria-activedescendant={activeId}
                        onClick={(e) => e.stopPropagation()}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                    />
                </div>
            )}
            <div className="searchable-select-options" role="listbox" id={listId} aria-label={ariaLabel || placeholder}>
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => (
                        <div
                            key={option.value}
                            id={`${listId}-${index}`}
                            className={`searchable-select-option ${option.value === value ? 'selected' : ''} ${option.value === activeValue ? 'keyboard-active' : ''} ${option.disabled ? 'disabled' : ''}`}
                            role="option"
                            aria-selected={option.value === value}
                            aria-disabled={option.disabled || undefined}
                            tabIndex={-1}
                            onMouseEnter={() => { if (!option.disabled) setActiveValue(option.value); }}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault(); event.stopPropagation(); handleSelect(option.value);
                                }
                            }}
                            data-qp-learning-id={learningTargetId}
                            data-qp-learning-option-key={option.learningOptionKey}
                            onClick={() => {
                                if (!option.disabled) handleSelect(option.value);
                            }}
                        >
                            {renderOptionContent(option)}
                        </div>
                    ))
                ) : (
                    <div className="searchable-select-no-results">
                        {labels.noData}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <div className={`qubepro-searchable-select searchable-select-container ${disabled ? 'disabled' : ''} ${requiredInvalid || ariaInvalid ? 'input-error' : ''}`} data-density={density} ref={wrapperRef}>
                {required && (
                    <select
                        className="searchable-select-validation-proxy"
                        value={value}
                        onChange={() => undefined}
                        required
                        disabled={disabled}
                        tabIndex={-1}
                        aria-hidden="true"
                        onInvalid={(event) => {
                            event.preventDefault();
                            setRequiredInvalid(true);
                            setIsOpen(true);
                            triggerRef.current?.focus();
                        }}
                    >
                        {options.map(option => (
                            <option key={option.value} value={option.value} disabled={option.disabled}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                )}
                <div
                    id={id}
                    ref={triggerRef}
                    className={`searchable-select-trigger ${isOpen ? 'is-open' : ''}`}
                    onClick={() => {
                        if (disabled) return;
                        if (!isOpen) setActiveValue(selectedOption && !selectedOption.disabled ? selectedOption.value : null);
                        setIsOpen(!isOpen);
                    }}
                    role="combobox"
                    aria-label={ariaLabel}
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? listId : undefined}
                    aria-haspopup="listbox"
                    aria-activedescendant={isOpen ? activeId : undefined}
                    aria-disabled={disabled || undefined}
                    aria-required={required || undefined}
                    aria-invalid={requiredInvalid || ariaInvalid || undefined}
                    aria-describedby={ariaDescribedBy}
                    title={selectedOption?.label}
                    tabIndex={disabled ? -1 : 0}
                    onKeyDown={(event) => {
                        if (disabled) return;
                        if (isOpen) { handleListKey(event); return; }
                        if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
                            event.preventDefault();
                            setActiveValue(selectedOption && !selectedOption.disabled ? selectedOption.value : null);
                            setIsOpen(true);
                        }
                        if (event.key === 'Escape') setIsOpen(false);
                    }}
                    data-qp-learning-id={learningTargetId}
                >
                    <span className="searchable-select-value">
                        {selectedOption ? renderOptionContent(selectedOption, false) : <span className="placeholder">{placeholder}</span>}
                    </span>
                    <span className="searchable-select-arrow">
                        <Icon name="expandMore" size={16} />
                    </span>
                </div>
            </div>

            {typeof document !== 'undefined' && dropdownContent &&
                createPortal(dropdownContent, document.body)}
        </>
    );
};
